import { useState, useEffect } from "react";
import { CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import CalendlyEmbed from "./CalendlyEmbed";

/* ====== INSERT POINT: BACKEND LEAD ENDPOINT ======
   Submissions POST to this endpoint which emails the team via Resend. */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEAD_ENDPOINT = `${BACKEND_URL}/api/submit-lead`;

/* ====== INSERT POINT: CLOUDFLARE TURNSTILE SITE KEY ======
   Replace empty string with your Turnstile site key to enable spam protection.
   When empty, the Turnstile widget renders nothing — form still works. */
const TURNSTILE_SITE_KEY = "";

const SPEND_OPTIONS = ["<$5K", "$5K-$25K", "$25K-$100K", "$100K+"];

const captureUrlParams = () => {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    const keys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
        "gclid",
        "fbclid",
        "li_fat_id",
    ];
    const out = {};
    keys.forEach((k) => {
        const v = sp.get(k);
        if (v) out[k] = v;
    });
    out.referrer = document.referrer || "";
    return out;
};

export const FallbackForm = () => {
    const [formData, setFormData] = useState({
        full_name: "",
        work_email: "",
        company: "",
        vertical: "",
        ad_spend: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [firstName, setFirstName] = useState("");

    useEffect(() => {
        if (TURNSTILE_SITE_KEY) {
            const id = "cf-turnstile-script";
            if (!document.getElementById(id)) {
                const s = document.createElement("script");
                s.id = id;
                s.src =
                    "https://challenges.cloudflare.com/turnstile/v0/api.js";
                s.async = true;
                s.defer = true;
                document.body.appendChild(s);
            }
        }
    }, []);

    const validate = () => {
        const e = {};
        if (!formData.full_name.trim()) e.full_name = "Required";
        if (!formData.work_email.trim()) e.work_email = "Required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.work_email))
            e.work_email = "Enter a valid email";
        if (!formData.company.trim()) e.company = "Required";
        if (!formData.vertical) e.vertical = "Pick one";
        if (!formData.ad_spend) e.ad_spend = "Pick one";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setSubmitError("");
        if (!validate()) return;

        setSubmitting(true);
        const payload = {
            ...formData,
            ...captureUrlParams(),
            submitted_at: new Date().toISOString(),
        };

        try {
            const res = await fetch(LEAD_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const body = await res.text();
                throw new Error(`Lead endpoint returned ${res.status}: ${body}`);
            }
            if (window.plausible) window.plausible("form_submitted");
            setFirstName(formData.full_name.split(" ")[0]);
            setSubmitted(true);
        } catch (err) {
            setSubmitError(
                "Could not submit right now. Please try again or email info@growthx.digital.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div
                className="bg-white border border-border rounded-xl p-6 md:p-10"
                data-testid="form-thank-you"
            >
                <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">
                        Thanks {firstName}. Pick a time below.
                    </h3>
                </div>
                <p className="text-slate-600 mb-6">
                    We&apos;ll review what you sent and confirm the audit scope
                    on the call.
                </p>
                <CalendlyEmbed testId="thankyou-calendly" />
            </div>
        );
    }

    const fieldClass = (k) =>
        `w-full bg-white border ${
            errors[k] ? "border-red-400" : "border-border"
        } rounded-md px-3.5 py-3 text-base outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all`;

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white border border-border rounded-xl p-6 md:p-8"
            data-testid="fallback-form"
            noValidate
        >
            <div className="mb-5">
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight">
                    Prefer to share details first?
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                    We&apos;ll get back within 24 hours with audit scope.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1.5 text-slate-700">
                        Full name
                    </label>
                    <input
                        type="text"
                        className={fieldClass("full_name")}
                        value={formData.full_name}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                full_name: e.target.value,
                            })
                        }
                        data-testid="form-full-name"
                    />
                    {errors.full_name && (
                        <p className="text-xs text-red-500 mt-1">
                            {errors.full_name}
                        </p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1.5 text-slate-700">
                        Work email
                    </label>
                    <input
                        type="email"
                        className={fieldClass("work_email")}
                        value={formData.work_email}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                work_email: e.target.value,
                            })
                        }
                        data-testid="form-work-email"
                    />
                    {errors.work_email && (
                        <p className="text-xs text-red-500 mt-1">
                            {errors.work_email}
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-4">
                <label className="block text-sm font-medium mb-1.5 text-slate-700">
                    Company name
                </label>
                <input
                    type="text"
                    className={fieldClass("company")}
                    value={formData.company}
                    onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                    }
                    data-testid="form-company"
                />
                {errors.company && (
                    <p className="text-xs text-red-500 mt-1">
                        {errors.company}
                    </p>
                )}
            </div>

            <div className="mt-5">
                <span className="block text-sm font-medium mb-2 text-slate-700">
                    Vertical
                </span>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        { value: "Ecommerce", label: "Ecommerce" },
                        { value: "B2B Lead-Gen", label: "B2B Lead-Gen" },
                    ].map((opt) => {
                        const active = formData.vertical === opt.value;
                        return (
                            <button
                                type="button"
                                key={opt.value}
                                onClick={() =>
                                    setFormData({
                                        ...formData,
                                        vertical: opt.value,
                                    })
                                }
                                className={`px-4 py-3 rounded-md border text-sm font-medium transition-all ${
                                    active
                                        ? "border-primary bg-primary/5 text-primary"
                                        : "border-border bg-white text-slate-700 hover:bg-muted"
                                }`}
                                data-testid={`form-vertical-${opt.value
                                    .toLowerCase()
                                    .replace(/\s|-/g, "")}`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                {errors.vertical && (
                    <p className="text-xs text-red-500 mt-1">
                        {errors.vertical}
                    </p>
                )}
            </div>

            <div className="mt-5">
                <label className="block text-sm font-medium mb-1.5 text-slate-700">
                    Monthly ad spend
                </label>
                <select
                    className={fieldClass("ad_spend")}
                    value={formData.ad_spend}
                    onChange={(e) =>
                        setFormData({ ...formData, ad_spend: e.target.value })
                    }
                    data-testid="form-ad-spend"
                >
                    <option value="">Select range</option>
                    {SPEND_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </select>
                {errors.ad_spend && (
                    <p className="text-xs text-red-500 mt-1">
                        {errors.ad_spend}
                    </p>
                )}
            </div>

            {/* ===== INSERT POINT: CLOUDFLARE TURNSTILE WIDGET =====
                Renders only when TURNSTILE_SITE_KEY is set above. */}
            {TURNSTILE_SITE_KEY && (
                <div
                    className="cf-turnstile mt-5"
                    data-sitekey={TURNSTILE_SITE_KEY}
                    data-testid="turnstile-widget"
                />
            )}

            {submitError && (
                <div
                    className="mt-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
                    data-testid="form-submit-error"
                >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{submitError}</span>
                </div>
            )}

            <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 py-3.5 font-medium text-base disabled:opacity-60 transition-all min-h-[44px]"
                data-testid="form-submit-btn"
            >
                {submitting ? "Sending…" : "Get my audit scope"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
        </form>
    );
};

export default FallbackForm;
